// frontend/src/pages/VideoAnalyticsDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayCircleOutline as PlayIcon,
  People as PeopleIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';

const VideoAnalyticsDetail = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [heatmapData, setHeatmapData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, [videoId]);

  const fetchAnalyticsData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // ヒートマップデータとエンゲージメントデータを並列で取得
      const [heatmapResponse, engagementResponse] = await Promise.all([
        axios.get(`${API_URL}/api/video-analytics/heatmap/${videoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/video-analytics/engagement/${videoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setHeatmapData(heatmapResponse.data);
      setEngagementData(engagementResponse.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('分析データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getHeatmapColor = (intensity) => {
    // intensity: 0-100の値
    if (intensity === 0) return '#f5f5f5';
    if (intensity < 20) return '#bbdefb';
    if (intensity < 40) return '#64b5f6';
    if (intensity < 60) return '#2196f3';
    if (intensity < 80) return '#1976d2';
    return '#0d47a1';
  };

  const getHeatmapLabel = (intensity) => {
    if (intensity === 0) return '視聴なし';
    if (intensity < 20) return '低';
    if (intensity < 40) return '中低';
    if (intensity < 60) return '中';
    if (intensity < 80) return '中高';
    return '高';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/video-analytics')}>
          戻る
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/video-analytics')}
        sx={{ mb: 2 }}
      >
        動画一覧に戻る
      </Button>

      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {heatmapData?.video?.title || '動画タイトル'}
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Chip
            icon={<AccessTimeIcon />}
            label={`動画時間: ${formatTime(heatmapData?.video?.duration || 0)}`}
            sx={{ mr: 2 }}
          />
          <Chip
            icon={<PeopleIcon />}
            label={`視聴者数: ${heatmapData?.unique_viewers || 0}人`}
            sx={{ mr: 2 }}
          />
          <Chip
            icon={<EventIcon />}
            label={`総イベント数: ${heatmapData?.total_events || 0}`}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 視聴ヒートマップ */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <TimelineIcon sx={{ mr: 1 }} />
            視聴ヒートマップ
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            動画の各部分がどれだけ視聴されているかを色で表示します
          </Typography>

          <Box sx={{ position: 'relative', mt: 3 }}>
            {/* ヒートマップの凡例 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption">視聴頻度:</Typography>
                {[0, 20, 40, 60, 80, 100].map((intensity) => (
                  <Tooltip key={intensity} title={getHeatmapLabel(intensity)}>
                    <Box
                      sx={{
                        width: 30,
                        height: 20,
                        backgroundColor: getHeatmapColor(intensity),
                        border: '1px solid #ddd',
                        cursor: 'pointer'
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            {/* ヒートマップバー */}
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: 60,
                border: '1px solid #ddd',
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              {heatmapData?.heatmap?.map((segment) => (
                <Tooltip
                  key={segment.segment}
                  title={
                    <Box>
                      <Typography variant="caption">
                        {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        視聴回数: {segment.count}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        強度: {getHeatmapLabel(segment.intensity)}
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: getHeatmapColor(segment.intensity),
                      transition: 'opacity 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>

            {/* タイムスタンプ */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption">0:00</Typography>
              <Typography variant="caption">
                {formatTime(heatmapData?.video?.duration || 0)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 時間帯別アクティビティ */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            時間帯別視聴パターン
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            24時間の視聴アクティビティ分布
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, mt: 2, flexWrap: 'wrap' }}>
            {heatmapData?.hourly_activity?.map((hourData) => {
              const maxCount = Math.max(...(heatmapData.hourly_activity.map(h => h.count) || [1]));
              const height = maxCount > 0 ? (hourData.count / maxCount) * 100 : 0;
              
              return (
                <Tooltip
                  key={hourData.hour}
                  title={`${hourData.hour}時: ${hourData.count}回`}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: `${height}%`,
                        backgroundColor: hourData.count > 0 ? '#2196f3' : '#f5f5f5',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: hourData.count > 0 ? '#1976d2' : '#f5f5f5'
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      {hourData.hour}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Paper>

      {/* エンゲージメント指標 */}
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          エンゲージメント指標
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h4">
                    {engagementData?.total_viewers || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  総視聴者数
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h4">
                    {engagementData?.completed_viewers || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  完了者数（90%以上視聴）
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PlayIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="h4">
                    {engagementData?.average_completion_rate || 0}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  平均完了率
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    平均一時停止回数
                  </Typography>
                  <Typography variant="h6">
                    {engagementData?.average_pause_count || 0}回
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    平均シーク回数
                  </Typography>
                  <Typography variant="h6">
                    {engagementData?.average_seek_count || 0}回
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 視聴者詳細 */}
        {engagementData?.viewer_details && engagementData.viewer_details.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              視聴者詳細（上位20名）
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Grid container spacing={2}>
                {engagementData.viewer_details.map((viewer, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={viewer.student_id}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          学生 #{index + 1}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">完了率:</Typography>
                            <Typography variant="caption" fontWeight="bold">
                              {Math.round(viewer.completion_rate)}%
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">一時停止:</Typography>
                            <Typography variant="caption">
                              {viewer.pause_count}回
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption">シーク:</Typography>
                            <Typography variant="caption">
                              {viewer.seek_count}回
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default VideoAnalyticsDetail;